import { DateTime } from 'luxon';
import { Alert, Button, Container, Spinner, Badge } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router';
import { getHotelReservation } from '../apis/hotelApis';
import { DEFAULT_TIMEZONE, PaymentMethod, language } from '../common/constants';
import { Loader } from '../controls/Loader';
import IconBack from '../icons/IconBack';
import IconDownload from '../icons/IconDownload';
import { getPageUrl, searchUrl, toQueryString } from '../utils/urlUtils';
import { StyledReservation, StyledReservationCard, StyledStayTimeGrid, StyledSuccessAlert } from './Reservation.styled';
import { StyledBreadcrumb, StyledCard, StyledDetailText, StyledCardTitle } from './Review.styled';
import { StyledHotelName } from './Search.styled';
import html2canvas from 'html2canvas';
import { CancelPolicy, CheckInPolicy } from '../modules/Policy';
import { BookingStatus, IHotelAvailability, PaymentStatus } from '../types/hotelTypes';
import { formatCurrency } from '../utils/numberUtil';
import { Helmet } from 'react-helmet';
import { useNewRelic } from '../hooks/useNewRelic';
import { PageAction } from '../types/newrelicTypes';
import { useContext, useEffect, useState } from 'react';
import IconSuccess from '../icons/IconSuccess';
import { HotelContext } from '../modules/HotelContext';
import { useScrollIntoViewIframe } from '../hooks/useScrollIntoView';
import { FormProvider, useForm } from 'react-hook-form';
import { createPaymentUrl } from '../apis/paymentApi';
import { PaymentForm } from '../modules/PaymentForm';
import { IVietQRRouteParams } from './VietQRPayment';
import { useQuery } from '@tanstack/react-query';
import { navigatorWebShare } from '../utils/iframeUtils';
import { LoyaltySummary } from '../modules/LoyaltySummary';
import { getDepositAmountVnd } from '../utils/paymentOptionUtils';

interface IRouteState {
  priceInfo: IHotelAvailability;
  paymentMethod: PaymentMethod;
}

interface IPaymentFormState {
  paymentMethod: PaymentMethod;
}

export const Reservation = () => {
  const { hotelId, reservationId } = useParams();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [blob, setBlob] = useState<Blob>();
  const { hotel } = useContext(HotelContext);
  const depositAmountVnd = getDepositAmountVnd(hotel.paymentOptions);

  const { priceInfo, paymentMethod = PaymentMethod.CheckIn } = (useLocation().state || {}) as IRouteState;
  const {
    data: reservation,
    error: reservationError,
    refetch: refetchReservation,
  } = useQuery({
    queryKey: [`/api/hotels/${hotelId}/reservation/${reservationId}`],
    queryFn: () => getHotelReservation(hotelId, reservationId),
  });
  const totalPrice = priceInfo?.TotalPrice || reservation?.TotalPrice;
  const handlePaymentRequest = async (totalPrice: number, paymentMethod: PaymentMethod, delay?: number) => {
    if (!totalPrice || !paymentMethod || paymentMethod === PaymentMethod.CheckIn) {
      return;
    }

    switch (paymentMethod) {
      case PaymentMethod.SecurityDeposit: {
        const search = toQueryString({
          amount: depositAmountVnd,
          hotelId: Number(hotelId),
          reservationId: Number(reservationId),
          paymentMethod,
          bookingNumber: reservation?.ReservationNumber,
        } as IVietQRRouteParams);
        navigate(`${getPageUrl(`/${hotelId}/payment/vietqr`)}?${search}`);
        return;
      }
      case PaymentMethod.VietQR:
      case PaymentMethod.VietQR_Cake_Email:
      case PaymentMethod.VietQR_Timo_Email:
      case PaymentMethod.VietQR_VietinBank: {
        const search = toQueryString({
          amount: totalPrice,
          hotelId: Number(hotelId),
          reservationId: Number(reservationId),
          paymentMethod,
          bookingNumber: reservation?.ReservationNumber,
        } as IVietQRRouteParams);
        navigate(`${getPageUrl(`/${hotelId}/payment/vietqr`)}?${search}`);
        return;
      }

      default:
        break;
    }

    setIsRedirecting(true);

    if (delay) {
      await new Promise<void>((resolve) => setTimeout(() => resolve(), delay));
    }

    try {
      const { url } = await createPaymentUrl({
        hotelId: Number(hotelId),
        reservationRoomId: Number(reservationId),
        amount: totalPrice,
        paymentMethod,
      });

      if (window.parent === window) {
        window.location.href = url;
      } else {
        window.parent.postMessage(
          {
            code: 'REDIRECT',
            href: url,
          },
          '*',
        );
      }
    } catch (err) {
      console.error(err);
      window.newrelic?.noticeError(err);
    } finally {
      setIsRedirecting(false);
    }
  };

  useEffect(() => {
    if (!reservation || reservation.PaymentStatus === PaymentStatus.Paid) {
      return;
    }

    handlePaymentRequest(totalPrice, paymentMethod, 3000);
  }, [priceInfo, reservation]);

  useEffect(() => {
    if (!reservation) {
      return;
    }

    doCreateImage();

    async function doCreateImage() {
      try {
        const content = document.getElementById('print-area');

        const canvas = await html2canvas(content, {
          ignoreElements: (el) => el.classList.contains('d-print-none') || el.classList.contains('html2canvas-ignore'),
        });
        const blob = await new Promise<Blob>((resolve) => canvas.toBlob((blob) => resolve(blob)));
        setBlob(blob);
      } catch (err) {
        console.error(err);
        window.newrelic?.noticeError(err);
      }
    }
  }, [reservation]);

  useNewRelic(hotel);
  useScrollIntoViewIframe(Boolean(reservation));
  const paymentForm = useForm<IPaymentFormState>({
    defaultValues: {
      paymentMethod,
    },
  });

  const handleChange = async () => {
    const paymentMethod = paymentForm.getValues('paymentMethod');

    await handlePaymentRequest(totalPrice, paymentMethod);
  };

  if (!reservation) {
    if (reservationError) {
      return (
        <StyledReservation>
          <Alert variant="danger">{language.GENERIC_ERROR}</Alert>
        </StyledReservation>
      );
    }

    return <Loader />;
  }

  const from = DateTime.fromISO(reservation.ArrivalDate, { zone: DEFAULT_TIMEZONE });
  const to = DateTime.fromISO(reservation.DepartureDate, { zone: DEFAULT_TIMEZONE });

  const goBack = () => {
    navigate(`${searchUrl()}/${hotelId}`);
  };

  const downloadPNG = async () => {
    setIsDownloading(true);

    try {
      const downloadFileName = `${reservation.ReservationNumber}-${DateTime.now().toFormat('yyyy-MM-dd')}.png`;
      await navigatorWebShare({
        files: [
          new File([blob], downloadFileName, {
            type: blob.type,
          }),
        ],
      });
      window.newrelic?.addPageAction(PageAction.DownloadReservation, {
        reservationId: reservationId,
        reservationNumber: reservation.ReservationNumber,
        from: new Date(reservation.ArrivalDate).getTime(),
        to: new Date(reservation.DepartureDate).getTime(),
      });
    } catch (err) {
      window.newrelic?.noticeError(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const isCancelled = reservation.BookingStatus === BookingStatus.CANCELLED;
  const isPaid = reservation.PaymentStatus === PaymentStatus.Paid;
  const isDeposit = reservation.PaymentStatus === PaymentStatus.Deposit;
  const shouldShowPaymentWarning = !isPaid && !isDeposit && hotel.disableRoomAssignment === true;

  return (
    <StyledReservation id="print-area">
      <Helmet>
        <title>{`Đặt phòng thành công - ${hotel?.hotelName}`}</title>
      </Helmet>
      <Container fluid>
        <StyledBreadcrumb className="d-print-none">
          <div className="left-breadcrumb">
            <Button size="sm" variant="link" onClick={goBack}>
              <IconBack />
            </Button>
            <StyledHotelName>{hotel.hotelName}</StyledHotelName>
          </div>
        </StyledBreadcrumb>
        {isRedirecting && <Loader fullScreen />}
        {!isPaid && !isDeposit && (
          <StyledSuccessAlert variant={shouldShowPaymentWarning ? 'warning' : 'success'} className="d-print-none">
            <div className="alert-content">
              <div className="alert-title">
                {shouldShowPaymentWarning ? 'Vui lòng thanh toán để hoàn tất đặt phòng' : 'Đặt phòng thành công.'}
              </div>
              {!shouldShowPaymentWarning && 'Cám ơn bạn đã đặt phòng ở Đào Tiên.'}
            </div>
            <div className="alert-icon">
              <IconSuccess />
            </div>
          </StyledSuccessAlert>
        )}
        {isPaid && (
          <StyledSuccessAlert variant="success" className="d-print-none">
            <div className="alert-content">
              <div className="alert-title">Thanh toán thành công.</div>
              Thanh toán bằng {reservation.PaymentMethod}.
            </div>
            <div className="alert-icon">
              <IconSuccess />
            </div>
          </StyledSuccessAlert>
        )}
        {isDeposit && (
          <StyledSuccessAlert variant="success" className="d-print-none">
            <div className="alert-content">
              <div className="alert-title">Thanh toán thành công.</div>
              Đã thanh toán {formatCurrency(depositAmountVnd)} — còn lại{' '}
              {formatCurrency(reservation.TotalPrice - depositAmountVnd)} thanh toán khi nhận phòng.
            </div>
            <div className="alert-icon">
              <IconSuccess />
            </div>
          </StyledSuccessAlert>
        )}
        <div className="reservation-content">
          <StyledReservationCard>
            <StyledCardTitle>
              <div>Thông tin đặt phòng</div>
              <div className="d-print-none">
                <Button size="sm" variant="link" title="Lưu về máy" disabled={isDownloading} onClick={downloadPNG}>
                  {isDownloading && <Spinner size="sm" className="mr-4" animation="border" variant="primary" />}
                  {!isDownloading && <IconDownload />}
                  <span style={{ marginLeft: 4 }}>Lưu về máy</span>
                </Button>
              </div>
            </StyledCardTitle>
            <StyledDetailText>
              <span>Mã đặt phòng: </span>
              <b>#{reservation.ReservationNumber}</b>
            </StyledDetailText>
            <StyledStayTimeGrid>
              <div className="stay-time-card">
                <div className="stay-time-label">Nhận phòng</div>
                <div className="stay-time-hour">{from.toFormat('HH:mm')}</div>
                <div className="stay-time-date">{from.toFormat('cccc, dd/MM/yyyy')}</div>
              </div>
              <div className="stay-time-arrow">→</div>
              <div className="stay-time-card">
                <div className="stay-time-label">Trả phòng</div>
                <div className="stay-time-hour">{to.toFormat('HH:mm')}</div>
                <div className="stay-time-date">{to.toFormat('cccc, dd/MM/yyyy')}</div>
              </div>
            </StyledStayTimeGrid>
            <StyledDetailText>
              <span>Khách sạn: </span>
              <b>{hotel.hotelName}</b>
            </StyledDetailText>
            <StyledDetailText>
              <span>Loại phòng: </span>
              <b>{reservation.RoomTypeName}</b>
            </StyledDetailText>
            {totalPrice && (
              <StyledDetailText>
                <span>Giá: </span>
                <b>{formatCurrency(totalPrice)}</b>
              </StyledDetailText>
            )}
            {isPaid && (
              <StyledDetailText>
                <span>Trạng thái: </span>
                <Badge pill bg="success">
                  Đã thanh toán
                </Badge>
              </StyledDetailText>
            )}
            {isDeposit && (
              <StyledDetailText>
                <span>Trạng thái: </span>
                <Badge pill bg="warning">
                  Thanh toán {formatCurrency(depositAmountVnd)}
                </Badge>
              </StyledDetailText>
            )}
            <hr />
            <StyledDetailText>
              <span>Số điện thoại: </span>
              <b>{reservation.Mobile}</b>
            </StyledDetailText>
            <StyledDetailText>
              <span>Tên liên hệ: </span>
              <b>{reservation.Fullname}</b>
            </StyledDetailText>
            {reservation.Note && (
              <StyledDetailText>
                <span>Các yêu cầu đặc biệt: </span>
                <b>{reservation.Note}</b>
              </StyledDetailText>
            )}
          </StyledReservationCard>
          {!isCancelled && !isPaid && !isDeposit && (
            <div className="d-print-none">
              <FormProvider {...paymentForm}>
                <PaymentForm
                  onChange={handleChange}
                  paymentOptions={hotel.paymentOptions}
                  webPaymentServiceId={hotel.webPaymentServiceId}
                />
              </FormProvider>
            </div>
          )}
          {reservation.loyalty && (
            <LoyaltySummary
              className="html2canvas-ignore"
              loyalty={reservation.loyalty}
              hotel={hotel}
              reservation={reservation}
              onRedeemed={async () => {
                await refetchReservation();
              }}
            />
          )}
          <CheckInPolicy />
          <CancelPolicy />
          <StyledCard>
            <StyledCardTitle>Liên hệ</StyledCardTitle>
            <p>
              - SĐT: {hotel.phoneNumber}
              <br />- Địa chỉ: {hotel.address}
            </p>
          </StyledCard>
        </div>
      </Container>
    </StyledReservation>
  );
};
